<script lang="ts">
    import FountainContentEditable from "../fountain/ScreenplayLiveMarginEditor.svelte";
    import ScreenplayPreview from "../fountain/ScreenplayPreview.svelte";
    import OutlineBoard from "../fountain/OutlineBoard.svelte";
    import { onMount } from "svelte";
    import EditIcon from "../components/icons/EditIcon.svelte";
    import IconButton from "../components/buttons/IconButton.svelte";
    import PreviewIcon from "../components/icons/PreviewIcon.svelte";
    import OutlineIcon from "../components/icons/OutlineIcon.svelte";

    export let projectId: string;
    export let identifier: string;
    export let setSavedState: (isSaved: boolean) => void;
    export let saveScreenplay: (screenplay: string) => Promise<void>;
    export let screenplay: string = "";
    export let allowSave: (() => boolean) | undefined;

    type TMode = "edit" | "preview" | "outline";

    let currentIndex: number = undefined;
    let mode: TMode = "edit";

    function setScreenplay(updatedScreenplay: string) {
        screenplay = updatedScreenplay;
    }

    let toggleTransferToParentFromEditor: () => void;
    let toggleTransferToParentFromOutline: () => void;

    function save() {
        saveScreenplay(screenplay)
            .then(() => {
                setSavedState(true);
            })
            .catch(() => {
                return;
            });
    }

    function updateScreenplayFromActiveMode() {
        if (mode === "edit") {
            toggleTransferToParentFromEditor();
        } else if (mode === "outline") {
            toggleTransferToParentFromOutline();
        }
    }

    function changeMode(newMode: TMode) {
        updateScreenplayFromActiveMode();
        mode = newMode;
    }

    function toggleUnsavedChanges() {
        setSavedState(false);
    }

    function editScreenplay(indexToEdit?: number): void {
        if (indexToEdit) {
            currentIndex = indexToEdit;
        }
        changeMode("edit");
    }

    function updateIndex(indexToEdit?: number): void {
        currentIndex = indexToEdit;
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.ctrlKey && e.key === "s") {
            e.preventDefault();
            e.stopPropagation();
            if (undefined === allowSave || allowSave()) {
                updateScreenplayFromActiveMode();
                save();
            }
        }
    }

    onMount(() => {
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    });
</script>

<div class="ScreenplayEditor" class:outline={mode === "outline"}>
    <div class="Header">
        <IconButton
            title="Edit screenplay"
            onClick={() => {
                changeMode("edit");
            }}
            icon={EditIcon}
            active={mode === "edit"}
        />
        <IconButton
            title="Outline"
            onClick={() => {
                changeMode("outline");
            }}
            icon={OutlineIcon}
            active={mode === "outline"}
        />
        <IconButton
            title="Preview screenplay"
            onClick={() => {
                changeMode("preview");
            }}
            icon={PreviewIcon}
            active={mode === "preview"}
        />
    </div>
    <div class="Editor">
        {#if mode === "edit"}
            <FountainContentEditable
                {identifier}
                {screenplay}
                {setScreenplay}
                {toggleUnsavedChanges}
                {currentIndex}
                {updateIndex}
                bind:transferToParent={toggleTransferToParentFromEditor}
            />
        {:else if mode === "preview"}
            <ScreenplayPreview {screenplay} {projectId} />
        {:else if mode === "outline"}
            <OutlineBoard
                {screenplay}
                {setScreenplay}
                {toggleUnsavedChanges}
                {editScreenplay}
                bind:transferToParent={toggleTransferToParentFromOutline}
            />
        {/if}
    </div>
</div>

<style type="text/scss">
    .ScreenplayEditor {
        position: relative;
        box-sizing: border-box;
        padding-top: 40px;
        .Header {
            position: fixed;
            top: 0;
            right: 0;
            z-index: 1;
            line-height: 24px;
            height: 40px;
            background: transparent;
            padding: 5px;
            display: flex;
            justify-content: space-between;
            box-sizing: border-box;
        }
        :global(button) {
            margin-left: 10px;
        }
    }
</style>
